import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';
import {
  customEmailValidator,
  FormSection,
  maxLengthValidator,
  numberValidator,
  requiredValidator,
} from 'src/app/shared/components/dynamic-form/form-sections.model';
import { ApiMethod, Endpoints } from '../utils/constants';
import { HttpService } from '../http/http.service';
import { Subject, takeUntil, map, Observable, of } from 'rxjs';
import { TranslationService } from 'src/app/shared/services/translation.service';

const statusTogglerMeta = {
  isBoolean: true,
  btnConfig: {
    1: { label: 'Yes', color: '' },
    0: { label: 'No', color: '' },
  },
};

@Injectable({
  providedIn: 'root',
})
export class ConfigurationService {
  staticText: any = TranslationService.staticTextData;
  fullMonthOptions: any = [
    { id: 1, display_name: 'January' },
    { id: 2, display_name: 'February' },
    { id: 3, display_name: 'March' },
    { id: 4, display_name: 'April' },
    { id: 5, display_name: 'May' },
    { id: 6, display_name: 'June' },
    { id: 7, display_name: 'July' },
    { id: 8, display_name: 'August' },
    { id: 9, display_name: 'September' },
    { id: 10, display_name: 'October' },
    { id: 11, display_name: 'November' },
    { id: 12, display_name: 'December' },
  ];
  brandConfig: FormSection[] = [
    {
      title: this.staticText?.setup_configuration?.branding_setup,
      key: 'branding_setup',
      columns: 4,
      sectionType: 'single', // This section allows multiple entries
      meta: {
        hideSectionTitle: true,
      },
      fields: [
        {
          type: 'text',
          label: this.staticText?.setup_configuration?.brand_form?.name,
          columns: 2,
          name: 'name',
          required: true,
          validators: [
            requiredValidator,
            maxLengthValidator(
              250,
              'Revenue Authority Name must be no longer than 250 characters.'
            ),
          ],
        },
        {
          type: 'file',
          label: this.staticText?.setup_configuration?.brand_form?.upload_logo,
          labelReq: false,
          name: 'country_logo',
          required: true,
          validators: [requiredValidator],
          meta: {
            table_display: false,
            max_file_size: 0.073,
            allowed_type: '.jpeg, .jpg, .png',
          },
        },
        {
          type: 'text',
          label: this.staticText?.setup_configuration?.brand_form?.copy_right,
          columns: 1,
          name: 'copyright',
          required: true,
          validators: [
            requiredValidator,
            maxLengthValidator(
              250,
              'Copyright Content must be no longer than 250 characters.'
            ),
          ],
        },
        {
          type: 'textarea',
          label: this.staticText?.setup_configuration?.brand_form?.address,
          columns: 1,
          name: 'organization_address',
          required: true,
          validators: [
            maxLengthValidator(
              500,
              'Address must be no longer than 500 characters.'
            ),
          ],
        },
        {
          type: 'textarea',
          label: this.staticText?.setup_configuration?.brand_form?.description,
          columns: 1,
          name: 'description',

          validators: [
            maxLengthValidator(
              500,
              'Description must be no longer than 500 characters.'
            ),
          ],
        },
      ],

    },
    {
      title: this.staticText?.setup_configuration?.brand_form?.footer_details,
      key: 'footer_icons',
      columns: 2,
      sectionType: 'multiple', 
      layout: 'table',
      meta: {
        btnText: this.staticText?.setup_configuration?.brand_form?.add_footer_detail,
        modalTitle: this.staticText?.setup_configuration?.brand_form?.footer_detail,
        delete : {
          title: this.staticText?.setup_configuration?.brand_form?.footer_delete,
          message: this.staticText?.setup_configuration?.brand_form?.footer_delete_msg
        },
        hideSectionTitle: false,
        uniqueFields: [
          { key: 'title', data_type: 'string' },
          
        ],
      },
      fields: [
        {
          type: 'text',
          label: this.staticText?.setup_configuration?.brand_form?.footer_title,
          name: 'title',
          validators: [
            requiredValidator,
            maxLengthValidator(
              250,
              'Title must be no longer than 250 characters.'
            ),
          ],
        },
        {
          type: 'text',
          label: this.staticText?.setup_configuration?.brand_form?.footer_url,
          name: 'url',
          validators: [
            requiredValidator,
            maxLengthValidator(
              250,
              'URL must be no longer than 250 characters.'
            ),
          ],
        },
        {
          type: 'text',
          label: this.staticText?.setup_configuration?.brand_form?.footer_class,
          name: 'class',
          validators: [
            requiredValidator,
            maxLengthValidator(
              250,
              'Class must be no longer than 250 characters.'
            ),
          ],
        },
      ],
    },

  ];

  geoConfig: FormSection[] = [
    {
      title: this.staticText?.setup_configuration?.geo_form?.title,
      key: 'geographical_classification',
      columns: 4,
      sectionType: 'single', // This section allows multiple entries
      meta: {
        hideSectionTitle: false,
      },
      fields: [
        {
          type: 'select',
          label: this.staticText?.setup_configuration?.geo_form?.country,
          name: 'country',
          required: true,
          validators: [requiredValidator],
          apiEndpoint: Endpoints.GET_COUNTRY_CONFIGURATION,
          meta: { onchangeCallback: () => { } },
        },
        {
          type: 'text',
          label: this.staticText?.setup_configuration?.geo_form?.isd_code,
          name: 'country_isd_code',
          validators: [requiredValidator],
        },
        {
          type: 'select',
          label: this.staticText?.setup_configuration?.geo_form?.timezone,
          name: 'timezone',
          required: true,
          validators: [requiredValidator],
          dependsOn: 'country',
          apiEndpoint: Endpoints.GET_TIMEZONE_CONFIGURATION,
        },
        {
          type: 'status_toggler',
          label:
            this.staticText?.setup_configuration?.geo_form
              ?.postal_code_required,
          name: 'is_postal_code_required',
          labelReq: true,
          meta: statusTogglerMeta,
        },
      ],
    },
    {
      title: this.staticText?.setup_configuration?.geo_form?.currencies,
      key: 'currency',
      columns: 4,
      sectionType: 'single',
      fields: [
        {
          type: 'text',
          label: this.staticText?.setup_configuration?.geo_form?.currency_type,
          name: 'currency_type',
          validators: [requiredValidator],
          options: [],
        },
      ],
    },
    {
      title: this.staticText?.setup_configuration?.geo_form?.title_2,
      key: 'geographical_hierarchy',
      columns: 1,

      sectionType: 'multiple', // This section allows multiple entries
      meta: {
        delete : {
          title: this.staticText?.setup_configuration?.geo_form?.delete_title,
          message: this.staticText?.setup_configuration?.geo_form?.delete_msg
        },
        inlineEdit: false,
        rowType: 'field', // field or fieldgroup
        btnText: 'Add Level',
        maxRow: 8,
        validatorField: true,
        makeFirstChildReadOnly: true,
      },
      layout: 'form',
      fields: [
        {
          type: 'text',
          label: this.staticText?.setup_configuration?.geo_form?.level,
          name: 'name',
          required: true,
          defaultValue: 'Country',
          validators: [requiredValidator],
          meta: {
            inputSuffixIcon: 'delete',
            inputSuffixFunction: () => { },
            allowInputSuffixToLastChild: true,
          },
        },
      ],
    },
  ];

  localizationConfig: FormSection[] = [
    {
      title: this.staticText?.setup_configuration?.localization_form?.title,
      key: 'languages',
      columns: 2,
      sectionType: 'multiple', // This section allows multiple entries
      layout: 'table',
      meta: {
        delete : {
          title: this.staticText?.setup_configuration?.localization_form?.delete_title,
          message: this.staticText?.setup_configuration?.localization_form?.delete_msg,
          main_event_button: this.staticText?.common?.delete
        },
        hideSectionTitle: false,
        modalTitle:
          this.staticText?.setup_configuration?.localization_form?.add_language,
        uniqueFields: [{ key: 'name', data_type: 'string' }],
      },
      fields: [
        {
          type: 'hidden',
          label: 'Default',
          name: 'is_default',
          labelReq: false,
          visible: false,
        },
        {
          type: 'text',
          label:
            this.staticText?.setup_configuration?.localization_form?.language,
          name: 'name',
          required: true,
          validators: [
            requiredValidator,
            maxLengthValidator(
              250,
              'Name must be no longer than 250 characters.'
            ),
          ],
        },
        {
          type: 'text',
          label: this.staticText?.setup_configuration?.localization_form?.code,
          name: 'code',
          required: true,
          validators: [
            requiredValidator,
            maxLengthValidator(
              250,
              'Code must be no longer than 250 characters.'
            ),
          ],
        },
        {
          type: 'file',
          label:
            this.staticText?.setup_configuration?.localization_form?.upload,
          labelReq: false,
          name: 'language_file',
          validators: [requiredValidator],
          meta: {
            max_file_size: 5,
            allowed_type: '.json',
          },
        },
        {
          type: 'download',
          label:
            this.staticText?.setup_configuration?.localization_form
              ?.download_template,
          labelReq: false,
          name: 'download',
          meta: {
            viewConfig: { visible: false },
            table_display: false,
            sourceLink: `${window.origin}/assets/i18n/en.json`,
            filename: 'language_template.json',
            displayText:
              this.staticText?.setup_configuration?.localization_form?.download,
          },
        },
      ],
    },
  ];

  accountingConfig: FormSection[] = [
    {
      title: this.staticText?.setup_configuration?.accounting_form?.title_1,
      key: 'accounting_setup',
      columns: 4,
      sectionType: 'single', // This section allows multiple entries
      meta: {
        hideSectionTitle: false,
      },
      fields: [
        {
          type: 'select',
          label:
            this.staticText?.setup_configuration?.accounting_form
              ?.finance_start,
          name: 'start_month',
          required: true,
          validators: [requiredValidator],
          options: this.fullMonthOptions,
          meta: { onchangeCallback: () => { } },
        },
        {
          type: 'select',
          label:
            this.staticText?.setup_configuration?.accounting_form?.finance_end,
          name: 'end_month',
          validators: [requiredValidator],
          options: this.fullMonthOptions,
        },
        {
          type: 'text',
          label:
            this.staticText?.setup_configuration?.accounting_form?.minimum_inventory_quantity,
          name: 'minimum_inventory_quantity',
          validators: [requiredValidator],
          labelReq: true,
          meta: {},
        }
      ],
    },
    {
      title: this.staticText?.setup_configuration?.accounting_form?.payment_mode,
      key: 'payment_section',
      columns: 4,
      sectionType: 'single',
      fields: [

        {
          type: 'select',
          label: this.staticText?.setup_configuration?.accounting_form?.payment_mode_label,
          name: 'payment_gateway',
          required: true,
          validators: [requiredValidator],
          options: [],
          meta: {
            ngSelect: true, multiple: true,
            dataMapping: (options: any, ele: any) => {

              if (ele?.apiData?.accounting_setup?.payment_gateway?.length) {

                const ids = ele?.apiData?.accounting_setup?.payment_gateway?.map((x: any) => x.id);
                const filterData = options?.data?.filter((x: any) => ids.includes(x.id));
                ele.apiData.accounting_setup.payment_gateway = filterData;
                ele.form?.get('payment_section.payment_gateway')?.setValue(filterData, { emitEvent: false })
              }

            }
          },
          apiEndpoint: `${Endpoints.PAYMENT_MODE}?is_config=true`,
        },
      ],
    },
    {
      title: this.staticText?.setup_configuration?.accounting_form?.shipment,
      key: 'is_shipment',
      columns: 4,
      sectionType: 'single',
      fields: [

        {
          type: 'number',
          label:
            this.staticText?.setup_configuration?.accounting_form?.vendor_po_delivery_date_lead_time,
          name: 'vendor_po_delivery_date_lead_time',
          required: true,
          validators: [requiredValidator],

        },
        {
          type: 'number',
          label:
            this.staticText?.setup_configuration?.accounting_form?.inventory_scan_max_stamps,
          name: 'inventory_scan_max_stamps',
          required: true,
          validators: [requiredValidator],

        },
        {
          type: 'number',
          label:
            this.staticText?.setup_configuration?.accounting_form?.inventory_scan_max_materials,
          name: 'inventory_scan_max_materials',
          required: true,
          validators: [requiredValidator],

        },


        // {
        //   type: 'status_toggler',
        //   label:
        //     this.staticText?.setup_configuration?.accounting_form?.shipment_label,
        //   name: 'is_shipment',
        //   labelReq: true,
        //   meta: statusTogglerMeta,
        // },


      ],
    }
  ];

  kycConfig: FormSection[] = [
    {
      title: this.staticText?.setup_configuration?.kyc_form?.tax_info,
      key: 'business_information',
      columns: 4,
      sectionType: 'single', // This section allows multiple entries

      meta: {
        hideSectionTitle: false,
      },
      fields: [
        {
          type: 'text',
          label:
            this.staticText?.setup_configuration?.kyc_form
              ?.bussiness_identity_label,
          name: 'business_indentification_label',
          required: true,
          validators: [requiredValidator],
          options: [],
        },
        {
          type: 'text',
          label:
            this.staticText?.setup_configuration?.kyc_form
              ?.bussiness_identity_format,
          name: 'business_indentification_number',
          required: true,
          validators: [requiredValidator],
          options: [],
        },
      ],
    },
    {
      title: this.staticText?.setup_configuration?.kyc_form?.title_2,
      meta: {
        hideSectionTitle: false,
      },
      key: 'tin_information',
      columns: 4,
      sectionType: 'single',
      fields: [
        {
          type: 'status_toggler',

          label: this.staticText?.setup_configuration?.kyc_form?.tin_required,
          name: 'is_tin_validation_required',
          labelReq: true,
          meta: { ...statusTogglerMeta, onchangeCallback: () => { } },
        },
        {
          type: 'status_toggler',
          label: this.staticText?.setup_configuration?.kyc_form?.otp_required,
          name: 'is_tin_otp_validation_required',
          labelReq: true,
          meta: statusTogglerMeta,
        },
        {
          type: 'text',
          label: this.staticText?.setup_configuration?.kyc_form?.api_url,
          name: 'tin_api_url',
          required: true,
          validators: [requiredValidator],
          options: [],
        },
        {
          type: 'text',
          label: this.staticText?.setup_configuration?.kyc_form?.tin_verify_auth_url,
          name: 'tin_verify_auth_url',
          required: true,
          validators: [requiredValidator],
          options: [],
        },
        {
          type: 'text',
          label: this.staticText?.setup_configuration?.kyc_form?.username,
          name: 'tin_api_username',
          required: true,
          validators: [requiredValidator],
          options: [],
        },
        {
          type: 'password',
          label: this.staticText?.setup_configuration?.kyc_form?.password,
          name: 'tin_api_password',
          required: true,
          validators: [requiredValidator],
          options: [],
          meta: {
            passwordvalue: true,
          },
        },
      ],
    },
    {
      title: this.staticText?.setup_configuration?.kyc_form?.title_3,
      key: 'tin_api_keys',
      columns: 2,
      sectionType: 'multiple', // This section allows multiple entries
      layout: 'table',
      meta: {
        delete : {
          title: this.staticText?.setup_configuration?.kyc_form?.delete_title,
          message: this.staticText?.setup_configuration?.kyc_form?.delete_msg
        },
        hideSectionTitle: false,
        uniqueFields: [{ key: 'database_key', data_type: 'object' }],
      },
      fields: [
        {
          type: 'hidden',
          label: 'Config ID',
          name: 'id',
          labelReq: false,
          visible: false,
        },
        {
          type: 'select',
          label: this.staticText?.setup_configuration?.kyc_form?.database_key,
          name: 'database_key',
          // validators: [requiredValidator],
          apiEndpoint: Endpoints.GET_DATABASE_KEY,
          meta: {
            passDatabaseKey: 'name',
          },
        },
        {
          type: 'text',
          label: this.staticText?.setup_configuration?.kyc_form?.response_key,
          name: 'response_key',
          validators: [
            // requiredValidator,
            maxLengthValidator(
              250,
              'Database Key must be no longer than 250 characters.'
            ),
          ],
        },
      ],
    },
    {
      title: this.staticText?.setup_configuration?.kyc_form?.ldap_title,
      key: 'ldap_information',
      columns: 4,
      sectionType: 'single', // This section allows multiple entries
      meta: {
        hideSectionTitle: false,
      },
      fields: [
        {
          type: 'status_toggler',
          columns: 1,
          label: this.staticText?.setup_configuration?.kyc_form?.ldap_required,
          name: 'is_ldap_enabled',
          labelReq: true,
          meta: { ...statusTogglerMeta, onchangeCallback: () => { } },
        },
        {
          type: 'text',
          label: `Connection URL`,
          name: 'ldap_connection_url',
          required: true,
          validators: [
            requiredValidator,
            maxLengthValidator(
              250,
              'Connection URL must be no longer than 250 characters.'
            ),
          ],
        },
        {
          type: 'text',
          label: `Bind Dn`,
          name: 'ldap_bind_dn',
          required: true,
          validators: [
            requiredValidator,
            maxLengthValidator(
              250,
              'Bind Dn must be no longer than 250 characters.'
            ),
          ],
        },
        {
          type: 'text',
          label: `Bind Credentials`,
          name: 'ldap_bind_credentials',
          required: true,
          validators: [
            requiredValidator,
            maxLengthValidator(
              250,
              'Bind Credentials must be no longer than 250 characters.'
            ),
          ],
        },
        {
          type: 'text',
          label: `Users DN`,
          name: 'ldap_users_dn',
          required: true,
          validators: [
            requiredValidator,
            maxLengthValidator(
              250,
              'Users DN must be no longer than 250 characters.'
            ),
          ],
        },
        {
          type: 'text',
          label: `Username Attribute`,
          name: 'username_ldap_attribute',
          required: true,
          validators: [
            requiredValidator,
            maxLengthValidator(
              250,
              'Username Attribute must be no longer than 250 characters.'
            ),
          ],
        },
        {
          type: 'text',
          label: `RDN`,
          name: 'rdn_ldap',
          required: true,
          validators: [
            requiredValidator,
            maxLengthValidator(
              250,
              'RDN must be no longer than 250 characters.'
            ),
          ],
        },
        {
          type: 'text',
          label: `UUID`,
          name: 'uuid_ldap',
          required: true,
          validators: [
            requiredValidator,
            maxLengthValidator(
              250,
              'UUID must be no longer than 250 characters.'
            ),
          ],
        },
      ],
    },
    {
      title: this.staticText?.setup_configuration?.kyc_form?.azure_title,
      key: 'ad_information',
      columns: 4,
      sectionType: 'single', // This section allows multiple entries
      meta: {
        hideSectionTitle: false,
      },
      fields: [
        {
          type: 'status_toggler',
          columns: 1,
          label: this.staticText?.setup_configuration?.kyc_form?.azure_required,
          name: 'is_ad_enabled',
          labelReq: true,
          meta: { ...statusTogglerMeta, onchangeCallback: () => { } },
        },
        {
          type: 'text',
          label: this.staticText?.setup_configuration?.kyc_form?.client_id,
          name: 'ad_client_id',
          required: true,
          validators: [
            requiredValidator,
            maxLengthValidator(
              250,
              'Client ID must be no longer than 250 characters.'
            ),
          ],
        },
        {
          type: 'text',
          label: this.staticText?.setup_configuration?.kyc_form?.client_secret,
          name: 'ad_client_secret',
          required: true,
          validators: [
            requiredValidator,
            maxLengthValidator(
              250,
              'Client Secret must be no longer than 250 characters.'
            ),
          ],
        },
        {
          type: 'text',
          label: this.staticText?.setup_configuration?.kyc_form?.tenant_id,
          name: 'ad_tenant_id',
          required: true,
          validators: [
            requiredValidator,
            maxLengthValidator(
              250,
              'Tenant Id must be no longer than 250 characters.'
            ),
          ],
        },
      ],
    },
    {
      title: this.staticText?.setup_configuration?.kyc_form?.title_4,
      key: 'admin_users',
      columns: 2,
      sectionType: 'multiple', // This section allows multiple entries
      meta: {
        hideDeleteAction: true,
        modalTitle: this.staticText?.setup_configuration?.kyc_form?.title_4,
        delete : {
          title: this.staticText?.setup_configuration?.kyc_form?.admin_delete_title,
          message: this.staticText?.setup_configuration?.kyc_form?.admin_delete_msg
        },
        inlineEdit: false,
        btnText: 'Add New User',
        hideSectionTitle: false,
        viewConfig: { action: true },
        noRecTemplate: `<div class="no_records_data">
        <img src="../../../../../assets/images/common/icons_usp/No records created.svg" alt="records_img"/>
        <div class="no_forcast_data">No entries have been added yet!</div>
        <div>`,
        uniqueFields: [{ key: 'email', data_type: 'string' }],
        maxRow: 1,
      },
      layout: 'table',
      fields: [
        {
          type: 'hidden',
          label: 'User ID',
          name: 'id',
          labelReq: false,
          visible: false,
        },
        {
          type: 'hidden',
          label: 'Default',
          name: 'is_default',
          labelReq: false,
          visible: false,
        },
        {
          type: 'text',
          label: this.staticText?.common?.first_name,
          name: 'first_name',
          required: true,
          validators: [requiredValidator],
        },
        {
          type: 'text',
          label: this.staticText?.common?.last_name,
          name: 'last_name',
          required: true,
          validators: [requiredValidator],
        },
        {
          type: 'text',
          label: this.staticText?.common?.email,
          name: 'email',
          required: true,
          validators: [
            requiredValidator,
            {
              name: 'email',
              validator: customEmailValidator(),
              message: 'Enter a valid email address format',
            },
          ],
        },
        {
          type: 'phone_field',
          label: this.staticText?.common?.contact_number,
          name: 'phone_number',
          altName: 'country_code',
          required: true,
          validators: [
            requiredValidator,
            {
              name: 'minlength',
              validator: Validators.minLength(3),
              message: 'Contact Number must be at least 3 characters long',
            },
            {
              name: 'maxlength',
              validator: Validators.maxLength(9),
              message: `Contact Number must be at least 9 characters long`,
            },
            {
              name: 'number',
              validator: numberValidator(),
              message: 'Enter a valid number.',
            },
          ],
        },
      ],
    },
  ];
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly _http: HttpService) { }

  private buildEndpoint(base: string, param?: any): string {
    return param ? `${base}${param}` : base;
  }

  private makeRequest(endpoint: any, method: ApiMethod): Observable<any> {
    return this._http.requestCall(endpoint, method).pipe(
      map((e: any) => e),
      takeUntil(this.destroy$)
    );
  }

  getDataBaseKey() {
    let endpoint: any = Endpoints.GET_DATABASE_KEY;
    return this.makeRequest(endpoint, ApiMethod.GET);
  }

  /**
   * @description
   * get configuration details................
   * @param param
   * @returns
   */
  getConfigurationDetails(resolvedData: any): Observable<any> {
    if (resolvedData) {
      return of({ data: resolvedData });
    } else {
      let endpoint: any = `${Endpoints.GET_INITAL_SETUP}`;
      return this._http.requestCall(endpoint, ApiMethod.GET).pipe(
        map((e: any) => e),
        takeUntil(this.destroy$)
      );
    }
  }

  getConfigurationLoginDetails(): Observable<any> {
    const endpoint: any = `${Endpoints.GET_INITAL_SETUP_LOGIN}`;
    return this._http
      .requestCall(endpoint, ApiMethod.GET)
      .pipe(map((e: any) => e));
  }

  /**
   * @description
   * Save configuration details ............................
   * @param id current config id
   * @param formData Payload details
   * @returns Return respective API response
   */

  saveDetails(formData: any) {
    let endpoint: any;
    endpoint = `${Endpoints.CREATE_INITAL_SETUP}`;
    return this._http.requestCall(endpoint, ApiMethod.POST, formData, {
      observe: 'response',
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  findMonthInterval(id: number, count: any = 12): any {
    // Base case: When count reaches 1, return the corresponding month
    if (count === 1) {
      return this?.fullMonthOptions?.find((month: any) => month.id === id);
    }

    // Recursive case: Calculate the next month's ID
    const nextId = id === 12 ? 1 : id + 1;

    // Recursive call with updated id and count
    return this.findMonthInterval(nextId, count - 1);
  }
}
